import { useEffect, useState } from 'react';
import { X, Wallet } from '../../lib/icons';
import { toast } from 'sonner';
import { PrintDoc } from '../print/PrintDoc';
import { attribuerNumero } from '../../lib/souches';
import {
  fetchCompteTiers,
  fetchFinances,
  enregistrerPaiementTiers,
  resumeImputation,
  MODES_PAIEMENT,
} from '../../lib/dataProvider';
import { fmt } from '../shared/badges';
import { useAuth } from '../../lib/auth';
import { caisseImposee } from '../../lib/permissions';
import { useTresorerieVerrou } from '../../lib/tresorerieEtat';

const field =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';

export const PaiementTiersDialog = ({ tiersId, onClose, onDone }) => {
  const { user } = useAuth();
  const { verrou: tresorerieKo, enCours: envoiEnCours, titre: titreVerrou } = useTresorerieVerrou();
  const caisseFixe = caisseImposee(user?.role);
  const [compte, setCompte] = useState(null);
  const [comptes, setComptes] = useState([]);
  const [f, setF] = useState({
    date: new Date().toISOString().slice(0, 10),
    sens: 'sortie',
    montant: '',
    mode: 'Espèces',
    compteId: caisseFixe ?? 'esp',
    piece: '',
    notes: '',
  });
  const [recu, setRecu] = useState(null);

  useEffect(() => {
    fetchCompteTiers(tiersId).then(setCompte);
    fetchFinances().then((fin) => {
      setComptes(caisseFixe ? fin.soldes.filter((c) => c.id === caisseFixe) : fin.soldes);
      if (!caisseFixe && fin.soldes.length && !fin.soldes.some((c) => c.id === 'esp'))
        setF((p) => ({ ...p, compteId: fin.soldes[0].id }));
    });
  }, [tiersId, caisseFixe]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const solde = compte?.solde ?? 0;
  const du = solde > 0 ? solde : 0; // l'huilerie doit au tiers
  const aRecevoir = solde < 0 ? -solde : 0; // le tiers doit à l'huilerie
  const montant = Number(f.montant) || 0;
  const estAvance = f.sens === 'sortie' ? montant > du : montant > aRecevoir;

  const submit = async (e) => {
    e.preventDefault();
    const { error, avance, row, imputation } = await enregistrerPaiementTiers({ ...f, tiersId });
    if (error) return toast.error(error);
    const nature = avance ? 'Avance' : f.sens === 'sortie' ? 'Paiement' : 'Encaissement';
    toast.success(`${nature} de ${fmt(montant, 3)} TND enregistré`);
    const nouveauSolde = f.sens === 'sortie' ? solde - montant : solde + montant;
    setRecu({
      numero: row?.piece && row.piece !== '—' ? row.piece : row?.id ?? '—',
      nature,
      sens: f.sens,
      montant,
      soldeAvant: solde,
      soldeApres: nouveauSolde,
      date: f.date,
      mode: f.mode,
      notes: f.notes,
      imputation,
    });
    onDone?.();
  };

  if (recu) {
    return (
      <PrintDoc
        type={`Reçu de ${recu.nature.toLowerCase()} — compte tiers`}
        numero={attribuerNumero('recu_paiement', recu.numero)}
        date={recu.date}
        tiers={compte?.nom ?? ''}
        lignes={[
          ['Nature de l’opération', recu.sens === 'sortie' ? `${recu.nature} versé par l’huilerie` : 'Encaissement reçu du tiers'],
          ['Mode de règlement', recu.mode],
          ['Solde avant opération', `${fmt(Math.abs(recu.soldeAvant), 3)} TND ${recu.soldeAvant >= 0 ? '(dû au tiers)' : '(dû par le tiers)'}`],
          ['Imputation sur les bons', resumeImputation(recu.imputation)],
          ['Notes / Justificatif', recu.notes || '—'],
        ]}
        totaux={[
          ['Montant de l’opération', `${fmt(recu.montant, 3)} TND`],
          ['Nouveau solde du compte', `${fmt(Math.abs(recu.soldeApres), 3)} TND`],
        ]}
        mentions="Reçu à signer par les deux parties. Montants exprimés en dinars tunisiens (TND)."
        onClose={onClose}
      />
    );
  }

  return (
    <div data-testid="paiement-tiers-overlay" className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4">
      <form onSubmit={submit} data-testid="paiement-tiers-form" className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-slate-200 bg-white">
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-black tracking-tight text-slate-900">
              <Wallet size={18} /> Régler / Avance
            </h2>
            <p data-testid="paiement-tiers-nom" className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
              {compte?.nom ?? '—'}
            </p>
          </div>
          <button type="button" data-testid="paiement-tiers-close" onClick={onClose} className="border border-slate-200 p-2 text-slate-500 transition-colors duration-150 hover:bg-slate-900 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div className="grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-3">
          {[
            ['Achats olives', fmt(compte?.totalAchats ?? 0, 3)],
            ['Prestations', fmt(compte?.totalPrestations ?? 0, 3)],
            ['Déjà payé', fmt(compte?.totalPaye ?? 0, 3)],
          ].map(([k, v]) => (
            <div key={k} className="bg-white px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.08em] text-slate-500">{k}</p>
              <p className="tabular font-data text-sm font-semibold text-slate-900">{v} TND</p>
            </div>
          ))}
        </div>

        <div data-testid="paiement-solde-actuel" className="border-y border-slate-200 bg-slate-50 px-5 py-4">
          <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Solde actuel du compte</p>
          <p className="tabular font-data text-2xl font-medium text-slate-900">
            {fmt(Math.abs(solde), 3)} <span className="text-sm text-slate-500">TND</span>
          </p>
          <p className="text-xs text-slate-600">
            {solde > 0
              ? 'À payer au tiers (achats non réglés)'
              : solde < 0
                ? 'Dû par le tiers à l’huilerie (prestations à encaisser)'
                : 'Compte soldé'}
          </p>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div>
            <label className={label}>Sens de l'opération</label>
            <div className="mt-2 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2">
              {[
                ['sortie', 'Paiement / Avance versée', 'L’huilerie donne de l’argent'],
                ['entree', 'Encaissement reçu', 'Le tiers paie l’huilerie'],
              ].map(([id, titre, hint]) => {
                const active = f.sens === id;
                return (
                  <button
                    key={id}
                    type="button"
                    data-testid={`paiement-sens-${id}`}
                    onClick={() => setF((p) => ({ ...p, sens: id }))}
                    className={`px-3 py-3 text-left transition-colors duration-150 ${active ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'}`}
                  >
                    <p className="text-sm font-bold">{titre}</p>
                    <p className={`text-xs ${active ? 'text-slate-300' : 'text-slate-500'}`}>{hint}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={label}>Date</label>
              <input type="date" data-testid="paiement-date" value={f.date} onChange={set('date')} className={field} />
            </div>
            <div>
              <label className={label}>Montant (TND) — saisie libre</label>
              <input
                data-testid="paiement-montant"
                type="number"
                step="0.001"
                min="0"
                value={f.montant}
                onChange={set('montant')}
                className={field}
                placeholder="Solde total, partiel ou avance"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="paiement-solder"
              onClick={() => setF((p) => ({ ...p, montant: String(p.sens === 'sortie' ? du : aRecevoir) }))}
              className="h-9 border border-slate-300 px-3 text-xs font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
            >
              Solder le compte
            </button>
            {estAvance && montant > 0 && (
              <span data-testid="paiement-badge-avance" className="flex h-9 items-center bg-amber-100 px-3 font-data text-xs font-bold uppercase tracking-[0.08em] text-amber-800">
                Avance — dépasse le solde
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className={label}>Mode</label>
              <select data-testid="paiement-mode" value={f.mode} onChange={set('mode')} className={field}>
                {MODES_PAIEMENT.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>Caisse / Banque</label>
              <select data-testid="paiement-compte" value={f.compteId} onChange={set('compteId')} disabled={Boolean(caisseFixe)} className={`${field} disabled:bg-slate-100`}>
                {comptes.map((c) => (
                  <option key={c.id} value={c.id}>{c.nom}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>N° pièce</label>
              <input data-testid="paiement-piece" value={f.piece} onChange={set('piece')} className={field} />
            </div>
          </div>

          <div>
            <label className={label}>Notes / Justificatif</label>
            <input
              data-testid="paiement-notes"
              value={f.notes}
              onChange={set('notes')}
              className={field}
              placeholder="ex. Avance pour paiement cueilleurs, retrait sur huile stockée…"
            />
          </div>
        </div>

        <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="submit"
            data-testid="paiement-submit"
            disabled={tresorerieKo}
            title={titreVerrou}
            className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {envoiEnCours ? 'Enregistrement en cours…' : tresorerieKo ? 'Trésorerie bloquée' : "Enregistrer l'opération"}
          </button>
          <button type="button" data-testid="paiement-cancel" onClick={onClose} className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100">
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};
